import { Injectable } from '@angular/core';
import { MetricSetDefinition, MetricDefinition } from './metric-set.models';

export interface ChartMetricSets {
  [setName: string]: MetricDefinition[];
}

@Injectable({
  providedIn: 'root',
})
export class ChartMetricsService {
  private metricSetDefinitions: MetricSetDefinition[] = [
    {
      name: 'overallShippingPerformance',
      detectKeys: [
        'orders_shipped',
        'shipments_delivered_early',
        'shipments_delivered_on_time',
        'shipments_delivered_late',
        'avg_time_to_ship',
        'avg_transit_time',
        'avg_delivery_time',
      ],
      metrics: [
        { key: 'orders_shipped', label: 'Shipped' },
        { key: 'shipments_delivered_early', label: 'Early' },
        { key: 'shipments_delivered_on_time', label: 'On time' },
        { key: 'shipments_delivered_late', label: 'Late' },
        {
          key: 'avg_time_to_ship',
          label: 'Shipping time',
          aggregationMethod: 'weightedAverage',
          weightMetricKey: 'orders_shipped',
        },
        {
          key: 'avg_transit_time',
          label: 'Transit time',
          aggregationMethod: 'weightedAverage',
          weightMetricKey: 'orders_shipped',
        },
        {
          key: 'avg_delivery_time',
          label: 'Delivery time',
          aggregationMethod: 'weightedAverage',
          weightMetricKey: 'orders_shipped',
        },
      ],
      dimensions: [
        { key: 'state', label: 'State', isDefault: true, priority: 1 },
        { key: 'city', label: 'City', priority: 2 },
        { key: 'courier', label: 'Courier', priority: 3 },
      ],
      defaultDimension: 'state',
    },
    {
      name: 'associatedCampaigns',
      detectKeys: [
        'user_count',
        'campaign_name',
        'order_source',
        'order_utm_source',
        'order_utm_campaign',
        'order_utm_medium',
      ],
      metrics: [{ key: 'user_count', label: 'User Count' }],
      dimensions: [
        { key: 'order_source', label: 'Source', isDefault: true, priority: 1 },
        { key: 'order_utm_source', label: 'UTM Source', priority: 2 },
        { key: 'order_utm_campaign', label: 'Campaign', priority: 3 },
        { key: 'order_utm_medium', label: 'Medium', priority: 4 },
        { key: 'campaign_name', label: 'Campaign Name', priority: 5 },
      ],
      defaultDimension: 'order_source',
    },
    {
      name: 'visitorsBySource',
      detectKeys: ['unique_users', 'source', 'utm_source', 'campaign', 'medium'],
      metrics: [{ key: 'unique_users', label: 'Visitors' }],
      dimensions: [
        { key: 'source', label: 'Source', isDefault: true, priority: 1 },
        { key: 'utm_source', label: 'UTM Source', priority: 2 },
        { key: 'campaign', label: 'Campaign', priority: 3 },
        { key: 'medium', label: 'Medium', priority: 4 },
      ],
      defaultDimension: 'source',
    },
  ];

  constructor() {}

  getMetricSetDefinition(name: string): MetricSetDefinition | undefined {
    return this.metricSetDefinitions.find(def => def.name === name);
  }

  registerMetricSetDefinition(definition: MetricSetDefinition): void {
    const existingIndex = this.metricSetDefinitions.findIndex(def => def.name === definition.name);

    if (existingIndex >= 0) {
      this.metricSetDefinitions[existingIndex] = definition;
    } else {
      this.metricSetDefinitions.push(definition);
    }
  }

  /**
   * Identify the metric set for the given data
   * @param data The data object to analyze
   * @returns The name of the identified metric set
   */
  identifyMetricSet(data: any): string {
    if (!data) return 'overallShippingPerformance';

    for (const definition of this.metricSetDefinitions) {
      const matchesDefinition = definition.detectKeys.some(key => data[key] !== undefined);
      if (matchesDefinition) {
        if (
          definition.metrics.some(m => data[m.key] !== undefined) ||
          definition.dimensions.some(d => data[d.key] !== undefined)
        ) {
          return definition.name;
        }
      }
    }

    if (
      data.orders_shipped !== undefined ||
      data.shipments_delivered_early !== undefined ||
      data.shipments_delivered_on_time !== undefined ||
      data.shipments_delivered_late !== undefined ||
      data.avg_time_to_ship !== undefined ||
      data.avg_transit_time !== undefined ||
      data.avg_delivery_time !== undefined
    ) {
      return 'overallShippingPerformance';
    }

    if (
      data.user_count !== undefined ||
      data.campaign_name !== undefined ||
      data.order_source !== undefined ||
      data.order_utm_source !== undefined ||
      data.order_utm_campaign !== undefined ||
      data.order_utm_medium !== undefined
    ) {
      return 'associatedCampaigns';
    }

    if (
      data.unique_users !== undefined &&
      (data.source !== undefined ||
        data.utm_source !== undefined ||
        data.campaign !== undefined ||
        data.medium !== undefined)
    ) {
      return 'visitorsBySource';
    }

    return 'overallShippingPerformance';
  }

  /**
   * Extract metrics from a data object based on the metric set
   * @param data The data object containing metrics
   * @param metricSetName Optional metric set name to use
   * @returns Array of extracted metrics
   */
  extractMetrics(data: any, metricSetName?: string): any[] {
    const actualMetricSetName = metricSetName || this.identifyMetricSet(data);
    const metricSet = this.getMetricSetDefinition(actualMetricSetName);

    if (metricSet) {
      return metricSet.metrics
        .filter(metricDef => data[metricDef.key] !== undefined)
        .map(metricDef => {
          const value = typeof data[metricDef.key] === 'number' ? data[metricDef.key] : 0;
          return {
            key: metricDef.key,
            title: metricDef.label,
            label: metricDef.label,
            value: value,
            formattedValue: metricDef.formatter ? metricDef.formatter(value) : value.toString(),
          };
        });
    }

    if (actualMetricSetName === 'overallShippingPerformance') {
      return this.extractShippingPerformanceMetrics(data);
    } else if (actualMetricSetName === 'associatedCampaigns') {
      return this.extractCampaignMetrics(data);
    } else if (actualMetricSetName === 'visitorsBySource') {
      return this.extractVisitorsBySourceMetrics(data);
    }

    return [];
  }

  /**
   * Legacy method to extract shipping performance metrics
   */
  private extractShippingPerformanceMetrics(data: any): Array<{
    key: string;
    title: string;
    label: string;
    value: number;
    formattedValue: string;
  }> {
    const metrics: Array<{
      key: string;
      title: string;
      label: string;
      value: number;
      formattedValue: string;
    }> = [];

    const originalMetricsMap: {
      [key: string]: { key: string; label: string };
    } = {
      orders_shipped: { key: 'orders_shipped', label: 'Shipped' },
      shipments_delivered_early: { key: 'shipments_delivered_early', label: 'Early' },
      shipments_delivered_on_time: { key: 'shipments_delivered_on_time', label: 'On time' },
      shipments_delivered_late: { key: 'shipments_delivered_late', label: 'Late' },
      avg_time_to_ship: { key: 'avg_time_to_ship', label: 'Shipping time' },
      avg_transit_time: { key: 'avg_transit_time', label: 'Transit time' },
      avg_delivery_time: { key: 'avg_delivery_time', label: 'Delivery time' },
    };

    Object.keys(originalMetricsMap).forEach(key => {
      if (data[key] !== undefined) {
        metrics.push({
          key: key,
          title: originalMetricsMap[key].label,
          label: originalMetricsMap[key].label,
          value: data[key],
          formattedValue:
            typeof data[key] === 'number' && (key.startsWith('avg_') || key.includes('time'))
              ? data[key].toFixed(1)
              : data[key].toString(),
        });
      }
    });

    return metrics;
  }

  /**
   * Legacy method to extract campaign metrics
   */
  private extractCampaignMetrics(data: any): any[] {
    const metrics = [];
    if (data.user_count !== undefined) {
      metrics.push({
        key: 'user_count',
        title: 'User Count',
        label: 'User Count',
        value: data.user_count,
        formattedValue: data.user_count.toString(),
      });
    }

    return metrics;
  }

  private extractVisitorsBySourceMetrics(data: any): any[] {
    const metrics = [];
    if (data.unique_users !== undefined) {
      metrics.push({
        key: 'unique_users',
        title: 'Visitors',
        label: 'Visitors',
        value: data.unique_users,
        formattedValue: data.unique_users.toString(),
      });
    }
    return metrics;
  }

  /**
   * Utility method to get a metric key from a title
   */
  getMetricKeyFromTitle(title: string): string {
    const titleMap: { [key: string]: string } = {
      Shipped: 'orders_shipped',
      Early: 'shipments_delivered_early',
      'On time': 'shipments_delivered_on_time',
      Late: 'shipments_delivered_late',
      'Shipping time': 'avg_time_to_ship',
      'Transit time': 'avg_transit_time',
      'Delivery time': 'avg_delivery_time',
      'User Count': 'user_count',
      Visitors: 'unique_users',
    };
    return titleMap[title] || title.toLowerCase().replace(/\s+/g, '_');
  }
}
